/**
 * Session 33 — device (browser) voice helpers for the AI copilot.
 *
 * Browser / WebView voices are free and differ per device, so the pick is
 * stored locally (not on the server). We rank the voices so the default is
 * the most natural-sounding English voice the device has.
 */
const VOICE_KEY = "nfc_ai_device_voice";
const RATE_KEY = "nfc_ai_device_voice_rate";
const PITCH_KEY = "nfc_ai_device_voice_pitch";

export const voiceSupported = () => typeof window !== "undefined" && !!window.speechSynthesis && typeof window.SpeechSynthesisUtterance !== "undefined";

export const getVoicePrefs = () => {
  try {
    const rate = parseFloat(localStorage.getItem(RATE_KEY));
    const pitch = parseFloat(localStorage.getItem(PITCH_KEY));
    return {
      voiceURI: localStorage.getItem(VOICE_KEY) || null,
      rate: Number.isFinite(rate) && rate >= 0.6 && rate <= 1.4 ? rate : 1,
      pitch: Number.isFinite(pitch) && pitch >= 0.7 && pitch <= 1.3 ? pitch : 1,
    };
  } catch (_) {
    return { voiceURI: null, rate: 1, pitch: 1 };
  }
};

export const setVoicePrefs = ({ voiceURI, rate, pitch }) => {
  try {
    if (voiceURI !== undefined) {
      if (voiceURI) localStorage.setItem(VOICE_KEY, voiceURI); else localStorage.removeItem(VOICE_KEY);
    }
    if (rate !== undefined) localStorage.setItem(RATE_KEY, String(rate));
    if (pitch !== undefined) localStorage.setItem(PITCH_KEY, String(pitch));
  } catch (_) { /* storage blocked */ }
  try { window.dispatchEvent(new CustomEvent("nfc-ai-voice-prefs")); } catch (_) { /* noop */ }
};

// Higher = nicer. Neural / "natural" voices first, then Google's, then by
// accent closeness to South African English.
export const scoreVoice = (v) => {
  const name = `${v.name || ""} ${v.voiceURI || ""}`.toLowerCase();
  const lang = (v.lang || "").toLowerCase().replace("_", "-");
  let score = 0;
  if (!lang.startsWith("en")) return -100;
  if (/natural|neural|premium|enhanced|wavenet|journey|studio/.test(name)) score += 40;
  if (/online/.test(name)) score += 10; // Edge "Online (Natural)" voices
  if (/google/.test(name)) score += 25;
  if (/microsoft/.test(name)) score += 12;
  if (/samantha|karen|daniel|moira|tessa|serena|ava|zoe|allison|susan|tom/.test(name)) score += 15; // Apple's better voices
  if (/compact|espeak|eloquence|novelty|bad news|bells|boing|bubbles|cellos|deranged|hysterical|pipe organ|trinoids|whisper|zarvox|albert|bahh|jester|wobble|good news|organ|fred|junior|ralph|kathy/.test(name)) score -= 60;
  if (lang === "en-za") score += 20;
  else if (lang === "en-gb") score += 14;
  else if (lang === "en-au" || lang === "en-nz" || lang === "en-ie") score += 10;
  else if (lang === "en-us") score += 8;
  else score += 2;
  if (v.localService) score += 1;
  return score;
};

export const rankVoices = (voices) => (voices || [])
  .filter((v) => (v.lang || "").toLowerCase().startsWith("en"))
  .map((v) => ({ voice: v, score: scoreVoice(v) }))
  .sort((a, b) => b.score - a.score || String(a.voice.name).localeCompare(String(b.voice.name)))
  .map((x) => x.voice);

/** Resolve voices (Chrome loads them async). */
export const loadVoices = () => new Promise((resolve) => {
  if (!voiceSupported()) { resolve([]); return; }
  const have = window.speechSynthesis.getVoices();
  if (have && have.length) { resolve(have); return; }
  let done = false;
  const finish = () => { if (done) return; done = true; resolve(window.speechSynthesis.getVoices() || []); };
  try { window.speechSynthesis.addEventListener("voiceschanged", finish, { once: true }); } catch (_) { window.speechSynthesis.onvoiceschanged = finish; }
  setTimeout(finish, 1200);
});

export const pickVoice = (voices, voiceURI) => {
  const list = voices || [];
  if (voiceURI) {
    const exact = list.find((v) => v.voiceURI === voiceURI || v.name === voiceURI);
    if (exact) return exact;
  }
  return rankVoices(list)[0] || null;
};

/** Friendly label for a SpeechSynthesisVoice. */
export const voiceLabel = (v) => {
  if (!v) return "Device default";
  const name = String(v.name || v.voiceURI || "Voice")
    .replace(/^Microsoft\s+/i, "")
    .replace(/\s*Online\s*\(Natural\)\s*-\s*English\s*\(([^)]+)\)/i, " · $1")
    .replace(/^Google\s+/i, "Google ");
  const lang = (v.lang || "").replace("_", "-");
  return lang ? `${name} (${lang})` : name;
};

let currentUtterance = null;

export const stopDeviceSpeech = () => {
  if (!voiceSupported()) return;
  try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
  currentUtterance = null;
};

/**
 * Speak with the device voice honouring the local preferences.
 * Returns a promise that resolves when speech ends (or immediately if
 * speech is not supported).
 */
export const speakWithDevice = (text, { onStart, voice } = {}) => new Promise((resolve) => {
  const clean = String(text || "").trim();
  if (!clean || !voiceSupported()) { resolve(false); return; }
  const prefs = getVoicePrefs();
  loadVoices().then((voices) => {
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(clean);
      const chosen = voice || pickVoice(voices, prefs.voiceURI);
      if (chosen) { utter.voice = chosen; utter.lang = chosen.lang || "en-ZA"; } else utter.lang = "en-ZA";
      utter.rate = prefs.rate;
      utter.pitch = prefs.pitch;
      let settled = false;
      const finish = (ok) => { if (settled) return; settled = true; if (currentUtterance === utter) currentUtterance = null; resolve(ok); };
      utter.onstart = () => { onStart && onStart(); };
      utter.onend = () => finish(true);
      utter.onerror = () => finish(false);
      currentUtterance = utter;
      window.speechSynthesis.speak(utter);
      // Some WebViews never fire onend for long text; guard with a timer.
      const guardMs = Math.min(120000, 4000 + clean.length * 90 / prefs.rate);
      setTimeout(() => finish(true), guardMs);
    } catch (_) {
      resolve(false);
    }
  });
});
